<script setup>
import { FUSO_ESCRITORIO } from 'dashboard/helper/fusoEscritorio';
// A SEMANA DESENHADA de um horário de atendimento (reformulação da Central,
// 26/09/2026 — referência: a grade de horário comercial do Yeastar P-Series e
// o "business hours" do Aircall). Uma linha por dia, régua de 0 a 24 h, cada
// faixa aberta pintada em teal; o dia de hoje destacado e um fio marcando a
// hora de agora (horário de Brasília, como o PBX decide). Lê o texto
// "Seg–Sex · 08:00–18:00" num relance, sem ter que montar a semana de cabeça.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { DIAS } from './destinos';

const props = defineProps({
  faixas: { type: Array, default: () => [] },
  agora: { type: Date, default: () => new Date() },
  aberto: { type: Boolean, default: false },
});

const { t } = useI18n();
const T = 'TELEFONIA.ROTEAMENTO.HORARIO';

const minutos = hhmm => {
  const [h, m] = String(hhmm || '0:0')
    .split(':')
    .map(Number);
  return h * 60 + (m || 0);
};
const pct = min => `${(Math.min(min, 1440) / 1440) * 100}%`;

// dia da semana e minuto de agora em Brasília
const hoje = computed(() => {
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone: FUSO_ESCRITORIO,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
    .formatToParts(props.agora)
    .reduce((acc, x) => ({ ...acc, [x.type]: x.value }), {});
  return {
    dia: p.weekday.toLowerCase().slice(0, 3),
    minuto: Number(p.hour) * 60 + Number(p.minute),
  };
});

const linhas = computed(() =>
  DIAS.map(dia => ({
    dia,
    hoje: dia === hoje.value.dia,
    blocos: props.faixas
      .filter(f => (f.dias || []).includes(dia))
      .map(f => {
        const de = minutos(f.de);
        const ate = Math.max(minutos(f.ate), de);
        return { de: f.de, ate: f.ate, left: pct(de), width: pct(ate - de) };
      }),
  }))
);
const MARCAS = [0, 6, 12, 18, 24];
const marca = h => t(`${T}.MARCA_HORA`, { h });
</script>

<template>
  <div class="semana" role="img" :aria-label="t(`${T}.SEMANA_ROTULO`)">
    <div class="semana__regua" aria-hidden="true">
      <span />
      <div class="semana__marcas">
        <span
          v-for="h in MARCAS"
          :key="h"
          class="semana__marca"
          :style="{ left: pct(h * 60) }"
          >{{ marca(h) }}</span
        >
      </div>
    </div>
    <div
      v-for="(l, i) in linhas"
      :key="l.dia"
      class="semana__linha"
      :class="{ 'semana__linha--hoje': l.hoje }"
    >
      <span class="semana__dia">{{ t(`${T}.DIAS.${l.dia}`) }}</span>
      <div class="semana__trilho">
        <span
          v-for="(b, k) in l.blocos"
          :key="k"
          v-tooltip="`${b.de}–${b.ate}`"
          class="semana__bloco"
          :style="{ left: b.left, width: b.width, '--i': i }"
        />
        <span
          v-if="l.hoje"
          class="semana__agora"
          :class="{ 'semana__agora--aberto': aberto }"
          :style="{ left: pct(hoje.minuto) }"
          aria-hidden="true"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.semana {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
}
.semana__regua,
.semana__linha {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  align-items: center;
  gap: var(--esp-2);
}
.semana__marcas {
  position: relative;
  height: var(--esp-3);
}
.semana__marca {
  position: absolute;
  top: 0;
  font-size: var(--txt-rotulo);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-10));
  transform: translateX(-50%);
}
.semana__marca:first-child {
  transform: none;
}
.semana__marca:last-child {
  transform: translateX(-100%);
}
.semana__dia {
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  text-transform: uppercase;
  letter-spacing: var(--rastro-rotulo);
  color: rgb(var(--slate-10));
}
.semana__linha--hoje .semana__dia {
  color: rgb(var(--blue-11));
}
.semana__trilho {
  position: relative;
  height: 14px;
  border-radius: var(--r-redondo);
  background: repeating-linear-gradient(
      90deg,
      transparent 0,
      transparent calc(25% - 1px),
      rgb(var(--slate-5)) calc(25% - 1px),
      rgb(var(--slate-5)) 25%
    ),
    rgb(var(--slate-3));
}
.semana__linha--hoje .semana__trilho {
  box-shadow: 0 0 0 1.5px rgb(var(--blue-7));
}
.semana__bloco {
  position: absolute;
  top: 0;
  bottom: 0;
  border-radius: var(--r-redondo);
  background: linear-gradient(90deg, rgb(var(--teal-8)), rgb(var(--teal-9)));
  box-shadow: 0 2px 6px -2px rgb(var(--teal-9) / 60%);
  transform-origin: left;
  animation: semana-cresce var(--mov-4) var(--mov-curva) both;
  animation-delay: calc(var(--i) * var(--cascata-passo));
}
@keyframes semana-cresce {
  from {
    transform: scaleX(0);
  }
}
.semana__agora {
  position: absolute;
  top: calc(var(--esp-1) * -1);
  bottom: calc(var(--esp-1) * -1);
  width: 2px;
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-11));
  transform: translateX(-50%);
}
.semana__agora::after {
  content: '';
  position: absolute;
  top: calc(var(--esp-1) * -1);
  left: 50%;
  width: 8px;
  height: 8px;
  border-radius: var(--r-redondo);
  background: inherit;
  transform: translateX(-50%);
}
.semana__agora--aberto {
  background: rgb(var(--teal-11));
}
@media (prefers-reduced-motion: reduce) {
  .semana__bloco {
    animation: none;
  }
}
</style>
